import merge from 'lodash.merge';
import { WidgetType } from 'types/widget.types';
import { generateDefaultData as generateDefaultMultipleChoiceData } from 'widgets/ResponsiveWidgets/MultipleChoicesWidget/MultipleChoicesWidget.helpers';
import { generateDefaultData as generateTextData } from 'widgets/TextBasedWidgets/TextWidget/TextWidget.helpers';
import { TextWidgetData } from 'widgets/TextBasedWidgets/TextWidget/TextWidget.types';

import { ComponentWidgetIdKeys, MultipleChoicesType, MultipleChoicesWidgetData } from '../MultipleChoicesWidget.types';

/**
 * Toggle Accordion expanded panel.
 *
 * @param indexes Initial or current expanded accordion indexes
 * @param valueToAdd New expanded panel
 * @returns {number[]} ExpandedIndexes
 */
export const toggleAccordionExpand = (indexes: number[], valueToAdd: number): number[] => {
  const index = indexes.indexOf(valueToAdd);
  if (index > -1) {
    const copy = [...indexes];
    copy.splice(index, 1);
    return copy;
  } else {
    return [...indexes, valueToAdd];
  }
};

/**
 * Create a new widget data using existing widget and group member data to use replaceWidget
 * @param operator
 * @param targetType
 * @param originWidgetData
 * @param memberWidgetIds
 * @param memberTextWidgetDataList
 * @returns
 */
export const mergeMultipleChoiceWidgetData = (
  operator: 'add' | 'delete',
  targetType: MultipleChoicesType,
  originWidgetData: MultipleChoicesWidgetData,
  memberWidgetIds: string[] = [],
  memberTextWidgetDataList: TextWidgetData[],
) => {
  const existingWidgetData = originWidgetData;
  const targetWidgetDefaultData = generateDefaultMultipleChoiceData(targetType);
  targetWidgetDefaultData.widgetData = merge(targetWidgetDefaultData.widgetData, existingWidgetData);

  // Set the exisiting questionWdiget
  const originQuestionTextWidgetData = memberTextWidgetDataList[0];
  targetWidgetDefaultData.groupWidgets = [
    {
      widgetType: WidgetType.Text,
      widgetData: originQuestionTextWidgetData as any,
      componentKey: ComponentWidgetIdKeys.QuestionText,
    },
  ];

  // Set the exisiting memberWidgets
  memberWidgetIds.forEach((_, index) => {
    // Ignore question text
    if (index === 0) return;

    const originTextWidgetData = memberTextWidgetDataList[index];
    if (originTextWidgetData) {
      targetWidgetDefaultData.groupWidgets.push({
        widgetType: WidgetType.Text,
        widgetData: originTextWidgetData as any,
        componentKey: ComponentWidgetIdKeys.AnswerText,
      });
    } else {
      targetWidgetDefaultData.groupWidgets.push({
        ...generateTextData(),
        componentKey: ComponentWidgetIdKeys.AnswerText,
      });
    }
  });

  if (operator === 'add') {
    // Add the one more widget
    targetWidgetDefaultData.groupWidgets.push({
      ...generateTextData(),
      componentKey: ComponentWidgetIdKeys.AnswerText,
    });

    // Increase the width px for the new choice
    targetWidgetDefaultData.widgetData = {
      ...targetWidgetDefaultData.widgetData,
      widthPx:
        targetWidgetDefaultData.widgetData.widthPx +
        targetWidgetDefaultData.groupWidgets[targetWidgetDefaultData.groupWidgets.length - 1].widgetData.widthPx,
    };
  }

  return targetWidgetDefaultData;
};

/**
 * Update correct answers order
 * @param correctAnswers {string[]} Correct answers
 * @param indexToRemove {number} Index to remove
 * @returns {string[]} Updated correct answers
 */
export const updateCorrectAnswers = (correctAnswers: string[], removeIndex: number): string[] => {
  if (removeIndex < 0 || !correctAnswers.length) return correctAnswers;

  return Array.from(correctAnswers)
    .filter((value) => value !== removeIndex.toString())
    .map((value) => {
      if (removeIndex < parseInt(value)) {
        return (parseInt(value) - 1).toString();
      } else return value;
    });
};
